<template>
  <Dashboard v-if="user.access_level < 1" title="Bootcamp" no-padding="true">
    <!-- show screenshot and push to upgrade -->
    <div class="w-full h-full bg-gray-950 relative flex justify-center items-center">
      <img
        class="select-none w-full h-full object-cover"
        src="../../assets/routines-preview.png"
      />
      <div
        class="w-full h-full bg-black/60 z-50 absolute items-center justify-center flex"
      >
        <UpgradeModal
          :title="$t('training.routines.upgrade_title')"
          tier="Player"
          :firstBenefit="$t('training.shared.refrag_academy')"
          :secondBenefit="$t('training.shared.refrag_bootcamp')"
          primaryPromotionType="bootcamp"
          video_url="https://www.youtube.com/embed/dC_uuBEZ9CI?si=fj-nBsQhEyA6asV2?autoplay=1"
        />
      </div>
    </div>
  </Dashboard>
  <Dashboard v-else title="Bootcamps">
    <div v-if="loading" class="flex justify-center h-full items-center">
      <Loader />
    </div>
    <div v-else class="flex flex-col gap-8 w-full">
      <div class="flex gap-10 items-center">
        <div class="flex flex-col gap-3 flex-1">
          <h1 class="text-4xl font-medium text-text-primary">{{ $t('training.routines.title') }}</h1>
          <p class="text-text-secondary text-sm font-light">
            {{ $t('training.routines.description') }}
          </p>
        </div>
        <div
          @click="createRoutine()"
          class="px-3 py-2.5 bg-primary-600 rounded-lg hover:bg-primary-500 duration-300 text-text-primary text-xs font-medium cursor-pointer"
        >
          {{ $t('training.routines.create') }}
        </div>
      </div>

      <div v-if="myRoutines?.length > 0" class="flex flex-col gap-4">
        <h1 class="text-2xl font-medium text-text-primary">{{ $t('training.routines.my_routines') }}</h1>
        <div
          class="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6"
        >
          <div
            v-for="routine in myRoutines"
            class="bg-gray-900 rounded-lg w-full flex flex-col overflow-hidden divide-y divide-gray-800"
          >
            <div class="flex gap-3 items-center p-4">
              <div class="flex flex-col gap-2 flex-1">
                <h1 class="text-text-primary">{{ routine.name }}</h1>
                <p class="text-text-secondary text-xs">
                  {{ $t('training.routines.created_on', { date: moment(routine.created_at).format("MMM Do YYYY") }) }}
                </p>
              </div>
              <div v-if="routine.status === 'published'" class="flex gap-2 items-center">
                <div
                  v-if="!routine.coach_curated"
                  @click="
                    router.push({
                      name: 'RoutineEditPage',
                      params: { slug: routine.slug },
                    })
                  "
                  class="cursor-pointer h-8 w-8 rounded-md bg-gray-800 flex items-center justify-center hover:bg-gray-700 duration-300"
                >
                  <PencilSquareIcon class="h-5 w-5 text-text-primary" />
                </div>
                <div
                  @click="launchRoutine(routine)"
                  class="cursor-pointer h-8 w-8 rounded-md bg-primary-600 flex items-center justify-center hover:bg-primary-500 duration-300"
                >
                  <svg
                    v-if="launchLoading === routine.id"
                    class="animate-spin h-full font-normal w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <HandThumbUpIcon
                    v-else-if="launchSuccess === routine.id"
                    class="text-green-500 w-4 h-4"
                  />
                  <img v-else class="brightness-200" src="../../assets/icons/play.svg" />
                </div>
              </div>

              <div v-else class="flex gap-2 items-center">
                <div
                  @click="toggleDeleteRoutineModal(routine)"
                  class="cursor-pointer h-8 w-8 rounded-md bg-gray-800 flex items-center justify-center hover:bg-gray-700 duration-300"
                >
                  <img class="brightness-200" src="../../assets/icons/trash.svg" />
                </div>
                <div
                  v-if="!routine.coach_curated"
                  @click="
                    router.push({
                      name: 'RoutineEditPage',
                      params: { slug: routine.slug },
                    })
                  "
                  class="cursor-pointer h-8 w-8 rounded-md bg-primary-600 flex items-center justify-center hover:bg-primary-500 duration-300"
                >
                  <PencilSquareIcon class="h-5 w-5 text-text-primary" />
                </div>
              </div>
            </div>
            <div class="flex justify-between w-full p-4">
              <div class="flex flex-col gap-2">
                <h1
                  class="text-text-secondary text-[10px] tracking-[1.5px] leading-[20px] uppercase"
                >
                  {{ $t('training.shared.author') }}
                </h1>
                <div class="flex gap-1 items-center">
                  <img
                    v-if="user.steam_profile?.avatarfull"
                    :src="user.steam_profile?.avatarfull"
                    class="w-4 h-4 rounded-full"
                  />
                  <h1 class="text-text-primary text-xs">{{ username() }}</h1>
                </div>
              </div>
              <div class="flex flex-col gap-2">
                <h1
                  class="text-text-secondary text-[10px] tracking-[1.5px] leading-[20px] uppercase"
                >
                  {{ $t('training.routines.status') }}
                </h1>
                <div class="flex gap-1 items-center">
                  <h1 class="text-text-primary text-xs uppercase">
                    {{ routine.status }}
                  </h1>
                </div>
              </div>
              <div class="flex flex-col gap-2">
                <h1
                  class="text-text-secondary text-[10px] tracking-[1.5px] leading-[20px] uppercase"
                >
                  {{ $t('common.length') }}
                </h1>
                <div class="flex gap-1 items-center">
                  <h1 class="text-text-primary text-xs">
                    {{ $t('training.routines.min_short', { n: routine.total_time_estimation || 0 }) }}
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div v-if="coachRoutines?.length > 0" class="col-span-2 xl:col-span-1 flex flex-col gap-4">
          <h1 class="text-2xl font-medium text-text-primary">{{ $t('training.routines.coach_routines') }}</h1>
          <CoachRoutine :routines="coachRoutines" @launchRoutine="launchRoutine" />
        </div>
        <div
          v-if="proRoutines?.length > 0"
          class="col-span-3 flex flex-col gap-4"
        >
          <h1 class="text-2xl font-medium text-text-primary">{{ $t('training.routines.pro_routines') }}</h1>
          <div class="relative w-full overflow-hidden">
            <button
              v-if="canScrollLeft"
              @click="scrollProRoutines('left')"
              class="absolute left-0 top-1/2 -translate-y-1/2 z-20 h-12 w-12 flex items-center justify-center bg-gray-700 rounded-full hover:bg-gray-600 duration-300 shadow-lg"
            >
              <ChevronLeftIcon class="h-6 w-6 text-text-primary" />
            </button>
            <div
              ref="proRoutinesCarousel"
              @scroll="updateProRoutinesScrollState"
              class="flex flex-nowrap gap-6 overflow-x-auto scrollbar-hide scroll-smooth pr-16"
              style="scrollbar-width: none; -ms-overflow-style: none;"
            >
              <div
                v-for="routine in proRoutines"
                :key="routine.id"
                class="bg-gray-900 rounded-lg flex-shrink-0 w-80 flex flex-col overflow-hidden relative"
              >
                <div
                  class="w-full aspect-[1.8] pro-card-gradient flex items-start justify-center overflow-hidden relative"
                >
                  <img :src="routine.image_url" :alt="$t('training.shared.alt_routine_image')" class="z-10" />
                </div>
                <div
                  @click="
                    router.push({ name: 'RoutinePage', params: { slug: routine.slug } })
                  "
                  class="bg-gray-800 rounded-lg h-6 w-6 absolute top-5 right-5 z-10 flex items-center justify-center hover:scale-105 duration-300 cursor-pointer hover:bg-gray-700"
                >
                  <img class="h-4" src="../../assets/icons/rightarrow.svg" />
                </div>
                <div class="p-4 flex flex-col gap-4 divide-y divide-gray-800">
                  <div class="flex flex-col gap-2">
                    <div class="flex items-center gap-2">
                      <h1 class="text-xl font-medium text-text-primary">
                        {{ routine.pro_player.name }}
                      </h1>
                      <span class="text-text-secondary">•</span>
                      <h1 class="text-xl font-medium text-text-primary">
                        {{ routine.pro_player.team_name }}
                      </h1>
                    </div>
                    <h1 class="text-text-primary">{{ routine.name }}</h1>
                  </div>

                  <div class="flex gap-2 items-center pt-4">
                    <button
                      :disabled="routine.enrolled"
                      @click="subscribe(routine)"
                      class="inline-flex gap-2 h-8 w-8 items-centerborder cursor-pointer border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500 flex justify-center items-center"
                    >
                      <PlusCircleIcon v-if="!routine.enrolled" class="w-3 h-3" />
                      <CheckCircleIcon v-else class="w-3 h-3" />
                    </button>
                    <button
                      @click="launchRoutine(routine)"
                      class="inline-flex h-8 px-2 text-xs text-text-primary bg-primary-600 hover:bg-primary-500 duration-300 cursor-pointer border-transparent rounded-lg items-center gap-1"
                    >
                      <svg
                        v-if="launchLoading === routine.id"
                        class="animate-spin h-full font-normal w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        />
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <HandThumbUpIcon
                        v-else-if="launchSuccess === routine.id"
                        class="text-green-500 w-6 h-6"
                      />
                      <PlayIcon v-else class="w-4 h-4 text-text-primary" />
                      {{ $t('training.shared.launch_routine') }}
                    </button>
                    <div class="flex-1" />
                    <div class="flex gap-1 items-center">
                      <ClockIcon class="w-3 h-3 text-text-secondary" />
                      <h2 class="text-text-secondary text-xs">
                        {{ $t('training.routines.min_spaced', { n: routine.total_time_estimation || 0 }) }}
                      </h2>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <button
              v-if="canScrollRight"
              @click="scrollProRoutines('right')"
              class="absolute right-0 top-1/2 -translate-y-1/2 z-20 h-12 w-12 flex items-center justify-center bg-gray-700 rounded-full hover:bg-gray-600 duration-300 shadow-lg"
            >
              <ChevronRightIcon class="h-6 w-6 text-text-primary" />
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex gap-4">
          <h1 class="text-2xl font-medium text-text-primary flex-1">{{ $t('training.routines.community') }}</h1>
          <div class="flex justify-end w-full flex-1 gap-4">
            <form @submit.prevent class="relative">
              <input
                type="search"
                v-model="searchInput"
                :placeholder="$t('training.shared.search_placeholder')"
                class="peer cursor-pointer text-sm font-light relative z-10 h-8 w-42 rounded-md bg-gray-800 border-gray-700 focus:border-gray-700 focus:outline-none focus:ring-0 pl-8 outline-none text-text-primary placeholder:text-text-secondary"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="absolute top-0 left-0 z-10 ml-2 translate-y-1/2 h-4 w-4 absolute"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#9CADC4"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </form>
          </div>
        </div>

        <div
          class="flex flex-col gap-1 lg:gap-[2px] rounded-lg w-full overflow-hidden"
          ref="contentBlock"
        >
          <ContentRow
            ref="contentRow"
            :content="content"
            :modUrl="'/routines'"
            :activeMod="'Routines'"
            :activeServer="activeServer"
            @updateServerInfo="updateServerInfo"
            @loadContent="loadContent"
            v-for="content in communityRoutines"
            :key="content.id"
          />
        </div>

        <!-- Pagination -->
        <div v-if="communityRoutines.length > 0" class="flex items-center justify-between gap-4 mt-4">
          <div class="flex items-center gap-2">
            <p class="text-text-secondary text-xs">{{ $t('training.routines.showing', { from: (page - 1) * 10 + 1, to: Math.min(page * 10, totalTaskLists), total: totalTaskLists }) }}</p>
          </div>

          <div v-if="totalPages > 1" class="flex items-center justify-end gap-2">
            <button
              class="p-2 text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:hover:text-text-secondary"
              :disabled="page === 1"
              @click="changePage(page - 1)"
            >
              <div class="flex bg-gray-700 p-2 rounded-lg">
                <ArrowLeftIcon class="h-4 w-4 text-text-secondary" />
              </div>
            </button>

            <button
              v-for="pageNum in displayedPages"
              :key="pageNum"
              @click="changePage(pageNum)"
              class="px-3 py-1 rounded-md text-sm"
              :class="[
                page === pageNum
                  ? 'text-primary-500'
                  : 'text-text-secondary hover:text-text-primary',
              ]"
            >
              {{ pageNum }}
            </button>

            <span v-if="hasEllipsis" class="text-text-secondary">...</span>

            <button
              v-if="showLastPage && !displayedPages.includes(totalPages)"
              @click="changePage(totalPages)"
              class="px-3 py-1 rounded-md text-sm text-text-secondary hover:text-text-primary"
              :class="{ 'bg-primary-600 text-text-primary': page === totalPages }"
            >
              {{ totalPages }}
            </button>

            <button
              class="p-2 text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:hover:text-text-secondary"
              :disabled="page === totalPages"
              @click="changePage(page + 1)"
            >
              <div class="flex bg-gray-700 p-2 rounded-lg">
                <ChevronRightIcon class="h-4 w-4 text-text-secondary" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Dashboard>
  <ModalWindow :open="showDeleteRoutineModal">
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-3">
        <h1
          class="text-text-primary text-[24px] font-semibold flex gap-3 items-center flex-1"
        >
          <img src="../../assets/icons/error.svg" :alt="$t('training.shared.alt_steam_icon')" class="w-8 h-8" />
          {{ $t('training.routines.delete_title', { name: selectedRoutine?.name }) }}
        </h1>
        <button
          @click="showDeleteRoutineModal = false"
          class="text-text-primary text-[24px] font-semibold cursor-pointer"
        >
          <XMarkIcon class="w-6 h-6 text-text-secondary" />
        </button>
      </div>

      <p class="text-text-secondary text-sm">
        {{ $t('training.routines.delete_confirm') }}
      </p>
      <div class="flex gap-4">
        <button
          @click="deleteRoutine(selectedRoutine)"
          class="bg-danger-400 text-danger-950 px-4 py-2 rounded-md font-semibold hover:bg-danger-300 w-full duration-300"
        >
          {{ $t('training.routines.delete_yes') }}
        </button>
        <button
          @click="showDeleteRoutineModal = false"
          class="bg-gray-800 text-text-primary px-4 py-2 rounded-md font-medium hover:bg-gray-700 w-full duration-300"
        >
          {{ $t('training.routines.delete_no') }}
        </button>
      </div>
    </div>
  </ModalWindow>
  <StartServerModal
    :server="serverInfo"
    :show="showServerModal"
    @close="showServerModal = false"
  />
</template>

<script setup>
import axios from 'axios';
import moment from 'moment';
import { useStore } from 'vuex';
import {
  computed, ref, watch, onMounted, onUnmounted, nextTick,
} from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useRouter, useRoute } from 'vue-router';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import {
  PencilSquareIcon,
  PlusCircleIcon,
  PlayIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from '@heroicons/vue/20/solid';
import Loader from '../../components/General/Loader.vue';
import CoachRoutine from '../../components/Coach/Routine.vue';
import ModalWindow from '../../components/ModalWindow.vue';
import ContentRow from '../../components/CommunityHub/ContentRow.vue';
import UpgradeModal from '../../components/UpgradeModal.vue';
import StartServerModal from '../../components/StartServerModal.vue';
import Dashboard from '../../components/layouts/DashboardLayout.vue';

const { t } = useI18n();
const auth = useAuth();
const router = useRouter();
const route = useRoute();
const store = useStore();
const user = ref(auth.user());
const communityRoutines = ref([]);
const myRoutines = ref([]);
const coachRoutines = ref([]);
const activeServer = computed(() => store.getters.activeServer);
const showServerModal = ref(false);
const launchLoading = ref(false);
const launchSuccess = ref(false);
const serverInfo = ref(null);
const proRoutines = ref([]);
const loading = ref(true);
const page = ref(1);
const searchInput = ref('');
const communitySearchLoading = ref(false);
const showDeleteRoutineModal = ref(false);
const selectedRoutine = ref(null);
const totalPages = ref(0);
const totalTaskLists = ref(0);
const windowWidth = ref(window.innerWidth);
const proRoutinesCarousel = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(true);

// Pagination computed properties
const displayedPages = computed(() => {
  const maxVisiblePages = 3;
  if (totalPages.value <= maxVisiblePages) {
    return Array.from({ length: totalPages.value }, (_, i) => i + 1);
  }

  let start = Math.max(1, page.value - Math.floor(maxVisiblePages / 2));
  const end = Math.min(start + maxVisiblePages - 1, totalPages.value);

  if (end - start + 1 < maxVisiblePages) {
    start = Math.max(1, end - maxVisiblePages + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const hasEllipsis = computed(() => {
  const maxVisiblePages = 3;
  return totalPages.value > maxVisiblePages
         && !displayedPages.value.includes(totalPages.value);
});

const showLastPage = computed(() => {
  const maxVisiblePages = 3;
  return totalPages.value > maxVisiblePages;
});

useHead({
  title: t('training.routines.page_title'),
  description: t('training.routines.page_description'),
});

function toggleDeleteRoutineModal(routine) {
  selectedRoutine.value = routine;
  showDeleteRoutineModal.value = !showDeleteRoutineModal.value;
}

function getBootcamps() {
  loading.value = true;
  axios.get('/task_lists?list_type=routine').then((res) => {
    communityRoutines.value = res.data.task_lists;
    totalPages.value = res.data.total_pages;
    totalTaskLists.value = res.data.task_list_count;
    loading.value = false;
  });

  axios.get('/task_lists/my_routines?list_type=routine').then((res) => {
    myRoutines.value = res.data;
  });

  axios.get('/task_lists/pro_routines?list_type=routine').then((res) => {
    proRoutines.value = res.data;
    nextTick(() => {
      updateProRoutinesScrollState();
      // DOM may not have final layout yet; re-check after render settles
      setTimeout(updateProRoutinesScrollState, 500);
    });
  });

  axios.get('/task_lists/my_daily_routine').then((res) => {
    coachRoutines.value = res.data;
  });
}

function updateServerInfo(info) {
  serverInfo.value = info;
  showServerModal.value = true;
}

function deleteRoutine(routine) {
  axios({
    method: 'delete',
    url: `/task_lists/${routine.id}`,
  }).then(() => {
    getBootcamps();
    showDeleteRoutineModal.value = false;
  });
}

function getCommunityContent() {
  axios({
    method: 'get',
    url: '/task_lists',
    params: {
      list_type: 'routine',
      page: page.value,
      search_input: searchInput.value,
    },
  }).then((res) => {
    communityRoutines.value = res.data.task_lists;
  });
}

function subscribe(routine) {
  axios({
    method: 'post',
    url: `/task_lists/${routine.slug}/enroll`,
  }).then(() => {
    getBootcamps();
  });
}

function loadContent() {
  getCommunityContent();
}

function createRoutine() {
  axios({
    method: 'post',
    url: '/task_lists',
  }).then((res) => {
    router.push({ name: 'RoutineEditPage', params: { slug: res.data.slug } });
  });
}

function username() {
  if (user.value?.username) {
    return user.value?.username;
  }
  return user.value?.steam_profile?.personaname;
}

function launchRoutine(bootcamp) {
  // Always open StartServerModal so the map/preset override panel shows, whether
  // the server is already live or still needs to be started. This mirrors the
  // individual routine page (RoutinePage.vue) behavior.
  serverInfo.value = {
    mode: 'Creator Studio',
    content: {
      task_list_id: bootcamp.id,
      is_exam: false,
      title: bootcamp.name,
      type: 'task',
      use_difficulty: 1,
      override_map_id: bootcamp.map_overrider ?? -1,
      override_preset_id: bootcamp.preset_loadout_id ?? -1,
    },
  };
  showServerModal.value = true;
}

function previousPage() {
  if (page.value > 1) {
    page.value--;
    getCommunityContent();
  }
}

function nextPage() {
  page.value++;
  getCommunityContent();
}

function changePage(newPage) {
  page.value = newPage;
  getCommunityContent();
}

function updateProRoutinesScrollState() {
  if (!proRoutinesCarousel.value) return;
  const container = proRoutinesCarousel.value;
  canScrollLeft.value = container.scrollLeft > 0;
  canScrollRight.value = container.scrollLeft < (container.scrollWidth - container.clientWidth - 5);
}

function scrollProRoutines(direction = 'right') {
  if (!proRoutinesCarousel.value) return;
  const container = proRoutinesCarousel.value;
  // w-80 = 320px, gap-6 = 24px, total = 344px per item
  const scrollAmount = 344;
  container.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth',
  });
  // Update scroll state after a short delay to allow smooth scrolling
  setTimeout(updateProRoutinesScrollState, 300);
}

watch(
  () => searchInput.value,
  () => {
    if (communitySearchLoading.value) return;
    communitySearchLoading.value = true;

    setTimeout(() => {
      communitySearchLoading.value = false;
      page.value = 1;
      getCommunityContent();
    }, 500);
  },
);

if (user.value.access_level > 0) {
  getBootcamps();
}

let proRoutinesResizeObserver = null;

onMounted(() => {
  nextTick(() => {
    if (proRoutinesCarousel.value) {
      proRoutinesResizeObserver = new ResizeObserver(updateProRoutinesScrollState);
      proRoutinesResizeObserver.observe(proRoutinesCarousel.value);
    }
  });
});

onUnmounted(() => {
  if (proRoutinesResizeObserver) proRoutinesResizeObserver.disconnect();
});
</script>

<style scoped>
.pro-card-gradient {
  background: linear-gradient(180deg, #131920 0%, #1f2833 100%);
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
